import React, { ReactElement } from 'react';

import * as Constants from 'Constants/Constants';
import * as Interfaces from 'Interfaces/Interfaces';

export const buildCutOffWarningMessage = (
  orderingRules: Interfaces.OrderingRules,
  isTeacher = false,
): string | null => {
  const orderRange = isTeacher ? orderingRules?.teacherOrderRange : orderingRules?.orderRange;
  const cutoffDay = isTeacher ? orderingRules?.teacherCutoffDay : orderingRules?.cutoffDay;

  const orderDueBy = isTeacher ? orderingRules?.teacherOrderDueBy : orderingRules?.orderDueBy;
  const orderDueByMonthly = isTeacher ? orderingRules?.teacherOrderDueByMonthly : orderingRules?.orderDueByMonthly;
  const cutoffTime = isTeacher ? orderingRules?.teacherCutoffTime : orderingRules?.cutoffTime;

  if (!orderRange || orderRange === Constants.ORDER_RANGE_OPTIONS.DAILY) {
    const ruleSuffix = Constants.ORDER_DUE_BY_RULES[orderDueBy as Constants.ORDER_DUE_BY_RULE_OPTIONS]?.ruleSuffix;
    return cutoffTime && ruleSuffix ? `Orders are due by ${cutoffTime} ${ruleSuffix}.` : null;
  } else if (orderRange === Constants.ORDER_RANGE_OPTIONS.WEEKLY) {
    const dayName = Constants.ORDER_CUT_OFF_DAYS[cutoffDay as string]?.name;
    return cutoffTime && dayName ? `Orders are due by ${cutoffTime} ${dayName} for orders next week.` : null;
  } else if (orderRange === Constants.ORDER_RANGE_OPTIONS.MONTHLY) {
    const monthName = Constants.ORDER_DUE_BY_MONTH[orderDueByMonthly as Constants.ORDER_DUE_BY_MONTH_OPTIONS]?.name;
    return cutoffTime && monthName ? `Orders are due by ${cutoffTime} ${monthName}.` : null;
  }

  return null;
};

export const buildMaxCutOffWarningMessage = (
  orderingRules: Interfaces.OrderingRules,
  isTeacher = false,
): string | null => {
  const maxOrderRange = isTeacher ? orderingRules?.teacherMaxOrderRange : orderingRules?.maxOrderRange;
  const maxCutoffDays = isTeacher ? orderingRules?.teacherMaxCutoffDays : orderingRules?.maxCutoffDays;
  const maxCutoffWeeks = isTeacher ? orderingRules?.teacherMaxCutoffWeeks : orderingRules?.maxCutoffWeeks;
  const maxCutoffMonths = isTeacher ? orderingRules?.teacherMaxCutoffMonths : orderingRules?.maxCutoffMonths;

  if (!maxOrderRange || maxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.DAYS) {
    const dayName = Constants.MAX_ORDER_UP_TO_DAYS[maxCutoffDays as Constants.MAX_ORDER_UP_TO_DAYS_OPTIONS]?.name;
    return dayName ? `Orders may be placed up to ${dayName}.` : null;
  } else if (maxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.WEEKS) {
    const weekName = Constants.MAX_ORDER_UP_TO_WEEKS[maxCutoffWeeks as Constants.MAX_ORDER_UP_TO_WEEKS_OPTIONS]?.name;
    return weekName ? `Orders may be placed up to ${weekName}.` : null;
  } else if (maxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.MONTHS) {
    const monthName =
      Constants.MAX_ORDER_UP_TO_MONTHS[maxCutoffMonths as Constants.MAX_ORDER_UP_TO_MONTHS_OPTIONS]?.name;
    return monthName ? `Orders may be placed up to ${monthName}.` : null;
  }

  return null;
};

const CutOffWarningMessage: React.FC<{ isTeacher?: boolean; orderingRules: Interfaces.OrderingRules }> = ({
  isTeacher = false,
  orderingRules,
}): ReactElement | null => {
  const message = buildCutOffWarningMessage(orderingRules, isTeacher);
  const maxMessage = buildMaxCutOffWarningMessage(orderingRules, isTeacher);

  return (
    <div>
      <div className="ordering-rules">{message}</div> <div className="ordering-rules">{maxMessage}</div>
    </div>
  );
};

export default CutOffWarningMessage;
